import type { ReactNode } from 'react'
import { FaExpand as ExpandIcon } from 'react-icons/fa6'
import { PianoRoll } from './PianoRoll'
import { AutomationLaneView } from './AutomationLane'
import { PluginPane } from './plugin-pane/PluginPane'
import { FullScreenModal } from './FullScreenModal'
import {
  LAYOUT_MODES,
  useSequencerStore,
  type LayoutMode,
} from '@/stores/sequencerStore'
import { useUIStore, type MainPaneTab } from '@/stores/uiStore'

/**
 * Tabs shell around the Sequencer's main pane.
 *
 * The first four buttons are the grid-layout modes — 2×8 / 8×8 / 4×4
 * / Tracker. Picking any of them does two things:
 *   1. sets `layoutMode` on the sequencer store (what the grid looks
 *      like)
 *   2. sets `mainPaneTab` to 'grid' (so Piano Roll / Automation /
 *      Plugin yield back to the grid view)
 *
 * Piano Roll, Automation, and Plugin sit to the right and swap the
 * grid out for a dedicated view.
 *
 * The trailing `⤢` button is a universal full-screen toggle — it
 * lifts the current tab's view into an in-page modal (see
 * `FullScreenModal`) without routing away. Works for every tab:
 * grid layouts, Piano Roll, Automation, Plugin.
 */
interface SequencerTabsProps {
  /** The existing step-grid view — rendered inside the grid tabs
   *  as-is. Passed as children so this component doesn't need to
   *  import the grid's deep component tree. */
  gridView: ReactNode
}

const VIEW_TABS = [
  { key: 'piano-roll', label: 'Piano Roll' },
  { key: 'automation', label: 'Automation' },
  { key: 'plugin', label: 'Plugin' },
] as const

/** Human-readable title for the full-screen modal header. Grid-tab
 *  titles defer to the active layout mode so the modal chrome says
 *  "TRACKER" instead of a bare "GRID" when that's what the user's
 *  looking at. */
function modalTitleFor(tab: MainPaneTab, layoutMode: LayoutMode): string {
  if (tab === 'grid') {
    const layout = LAYOUT_MODES.find(m => m.id === layoutMode)
    return layout?.label ?? 'GRID'
  }
  const view = VIEW_TABS.find(v => v.key === tab)
  return view?.label.toUpperCase() ?? tab.toUpperCase()
}

export function SequencerTabs({ gridView }: SequencerTabsProps) {
  const layoutMode = useSequencerStore(s => s.layoutMode)
  const setLayoutMode = useSequencerStore(s => s.setLayoutMode)
  const mainPaneTab = useUIStore(s => s.mainPaneTab)
  const setMainPaneTab = useUIStore(s => s.setMainPaneTab)
  const fullScreen = useUIStore(s => s.fullScreen)
  const setFullScreen = useUIStore(s => s.setFullScreen)

  const onLayoutClick = (id: LayoutMode) => {
    setLayoutMode(id)
    if (mainPaneTab !== 'grid') setMainPaneTab('grid')
  }

  // Render the active tab's body. Used for both the in-place slot
  // and the full-screen modal so tab switches keep working even
  // while full-screen is open. Grid view is passed as a prop rather
  // than imported so SequencerTabs doesn't have to know the page's
  // own StepGrid wiring.
  //
  // Note: rendering this twice (in-place + modal) does double the
  // React work for the active tab, but the underlying Zustand
  // subscriptions dedupe and the heaviest children (StepGrid,
  // SpectrumAnalyzer) already memoise their inputs. Simpler than
  // portalling the existing mount.
  const renderActive = () => {
    if (mainPaneTab === 'grid') return gridView
    if (mainPaneTab === 'piano-roll')
      return <PianoRoll fullScreen={fullScreen} />
    if (mainPaneTab === 'automation')
      return <AutomationLaneView fullScreen={fullScreen} />
    if (mainPaneTab === 'plugin') return <PluginPane fullScreen={fullScreen} />
    return null
  }

  return (
    // `flex-1 min-h-0` so the tabs container fills whatever vertical
    // space its grid/flex parent hands it — avoids the "big empty
    // band below an empty tab" problem where Plugin / Automation's
    // default panel sat at its minimum height while the right-side
    // columns (FX rail) stretched full height.
    <div className="flex flex-col gap-2 flex-1 min-h-0">
      <nav
        role="tablist"
        aria-label="Sequencer views"
        className="inline-flex items-center gap-0.5 rounded-lg bg-surface-sunken p-0.5 border border-brd-subtle self-start"
      >
        {LAYOUT_MODES.map(({ id, label }) => {
          // Layout buttons read active ONLY when the Grid tab is the
          // current view — switching to Piano Roll / Automation /
          // Plugin visually neutralises all four so the user can see
          // at a glance which non-grid tab is active.
          const active = mainPaneTab === 'grid' && layoutMode === id
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onLayoutClick(id)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition-colors ${
                active
                  ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                  : 'text-content-secondary hover:text-content-primary'
              }`}
            >
              {label}
            </button>
          )
        })}

        {/* Divider between layout-mode group and view-mode group. */}
        <span aria-hidden="true" className="mx-1 h-4 w-px bg-brd-subtle" />

        {VIEW_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={mainPaneTab === t.key}
            onClick={() => setMainPaneTab(t.key)}
            className={`px-3 py-1 rounded-md text-xs font-mono font-medium uppercase tracking-wider transition-colors duration-150 ${
              mainPaneTab === t.key
                ? 'bg-surface-raised text-content-primary shadow-low'
                : 'text-content-secondary hover:text-content-primary'
            }`}
          >
            {t.label}
          </button>
        ))}

        {/* Universal full-screen toggle — works for every tab. Opens
            an in-page modal via `FullScreenModal`; the route-based
            /werkshop/tools/* pages stay reachable for deep links
            but are no longer linked from the tab row. */}
        <span aria-hidden="true" className="mx-1 h-4 w-px bg-brd-subtle" />
        <button
          type="button"
          onClick={() => setFullScreen(true)}
          title="Expand this view to full screen (ESC to close)"
          aria-label="Enter full-screen view"
          className="px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider text-content-tertiary hover:text-accent flex items-center gap-1.5"
        >
          <ExpandIcon size={10} />
          Full screen
        </button>
      </nav>

      <div role="tabpanel" className="flex flex-1 min-h-0 flex-col">
        {!fullScreen && renderActive()}
      </div>

      <FullScreenModal
        open={fullScreen}
        onClose={() => setFullScreen(false)}
        title={modalTitleFor(mainPaneTab, layoutMode)}
      >
        {renderActive()}
      </FullScreenModal>
    </div>
  )
}
